import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSun, FiCloud, FiCloudRain, FiThermometer, FiWind, FiDroplet, FiAlertCircle, FiAlertTriangle, FiCheckCircle, FiHelpCircle, FiSunrise, FiSunset, FiChevronDown, FiChevronUp, FiExternalLink } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

/**
 * WeatherBadge - Weather display component for round cards
 * EXACTLY matches SmartResultCard's weather grid layout
 */
const WeatherBadge = ({
  golfScore,
  golfCategory,
  temperature,
  windSpeed,
  precipitation,
  precipitationLabel,  // 'Rain during round' or 'Rain on the day'
  humidity,
  isUnplayable,
  groundCategory,
  groundRainfall,      // Rainfall amount for ground quality description
  groundHistoryDays,   // Number of days for ground quality history
  uvIndex,             // UV index (0-11+) from WeatherKit
  alerts,              // Array of weather alerts from WeatherKit
  minuteForecast,      // Array of minute-by-minute precipitation (next hour)
  daylightCategory,    // 6-tier category from sunService
  daylightScore,       // 0-1 score (unused, kept for compatibility)
  sunrise,             // ISO timestamp or HH:MM string
  sunset,              // ISO timestamp or HH:MM string
  compact = false,
  compactSize = 'sm',
  wide = false,
}) => {
  // 6-tier category colour mapping for weather metrics (EXACT match to SmartResultCard)
  // Design System: Uses amber-* for all warning states (never orange/yellow)
  const getWeatherMetricColor = (category) => {
    switch (category) {
      case 'excellent': return 'border-emerald-700 bg-emerald-50 text-emerald-800';
      case 'very-good': return 'border-emerald-600 bg-emerald-50 text-emerald-700';
      case 'ideal': return 'border-emerald-700 bg-emerald-50 text-emerald-800'; // Legacy support
      case 'good': return 'border-amber-500 bg-amber-50 text-amber-700';
      case 'okay': return 'border-amber-600 bg-amber-50 text-amber-600';
      case 'fair': return 'border-amber-600 bg-amber-50 text-amber-600'; // Legacy support
      case 'poor': return 'border-amber-700 bg-amber-50 text-amber-700';
      case 'severe': return 'border-red-700 bg-red-50 text-red-700';
      default: return 'border-amber-600 bg-amber-50 text-amber-600';
    }
  };

  // Helper for getting icon colours by category (EXACT match to SmartResultCard)
  // Design System: Uses amber-* for all warning states (never orange/yellow)
  const getCategoryIconColor = (category) => {
    switch (category) {
      case 'excellent':
      case 'ideal': return 'text-emerald-800';
      case 'very-good': return 'text-emerald-700';
      case 'good': return 'text-amber-700';
      case 'okay':
      case 'fair': return 'text-amber-600';
      case 'poor': return 'text-amber-700';
      case 'severe': return 'text-red-700';
      default: return 'text-amber-600';
    }
  };

  // Helper for getting text colours by category (EXACT match to SmartResultCard)
  // Design System: Uses amber-* for all warning states (never orange/yellow)
  const getCategoryTextColor = (category) => {
    switch (category) {
      case 'excellent':
      case 'ideal': return 'text-emerald-800';
      case 'very-good': return 'text-emerald-700';
      case 'good': return 'text-amber-700';
      case 'okay':
      case 'fair': return 'text-amber-600';
      case 'poor': return 'text-amber-700';
      case 'severe': return 'text-red-700';
      default: return 'text-amber-600';
    }
  };

  // Get weather metric category (same thresholds as SmartResultCard)
  const getWeatherMetricCategory = (value, type) => {
    if (value === null || value === undefined) return 'unknown';

    switch (type) {
      case 'temperature':
        if (value >= 18 && value <= 24) return 'excellent';
        if (value >= 15 && value <= 27) return 'very-good';
        if (value >= 13 && value <= 29) return 'good';
        if (value >= 7 && value <= 32) return 'okay';
        if (value >= 2 && value <= 35) return 'poor';
        return 'severe';

      case 'windSpeed':
        if (value <= 5) return 'excellent';
        if (value <= 10) return 'very-good';
        if (value <= 15) return 'good';
        if (value <= 22) return 'okay';
        if (value <= 30) return 'poor';
        return 'severe';

      case 'precipitation':
        if (value <= 0.5) return 'excellent';
        if (value <= 2) return 'very-good';
        if (value <= 5) return 'good';
        if (value <= 10) return 'okay';
        if (value <= 20) return 'poor';
        return 'severe';

      case 'humidity':
        if (value >= 45 && value <= 65) return 'excellent';
        if (value >= 40 && value <= 70) return 'very-good';
        if (value >= 35 && value <= 75) return 'good';
        if (value >= 25 && value <= 85) return 'okay';
        if (value >= 15 && value <= 92) return 'poor';
        return 'severe';

      case 'uvIndex':
        if (value <= 2) return 'excellent';
        if (value <= 5) return 'good';
        if (value <= 7) return 'okay';
        if (value <= 10) return 'poor';
        return 'severe';

      default:
        return 'unknown';
    }
  };

  // Ground quality helpers (EXACT match to SmartResultCard)
  // Design System: Uses amber-* for all warning states (never orange/yellow)
  const getGroundQualityColor = (category) => {
    switch(category) {
      case 'firm': return 'border-emerald-700 bg-emerald-50 text-emerald-800';
      case 'soft': return 'border-amber-600 bg-amber-50 text-amber-600';
      case 'wet': return 'border-amber-700 bg-amber-50 text-amber-700';
      case 'waterlogged': return 'border-red-700 bg-red-50 text-red-700';
      default: return 'border-amber-600 bg-amber-50 text-amber-600';
    }
  };

  const getGroundQualityIconColor = (category) => {
    switch(category) {
      case 'firm': return 'text-emerald-800';
      case 'soft': return 'text-amber-600';
      case 'wet': return 'text-amber-700';
      case 'waterlogged': return 'text-red-700';
      default: return 'text-amber-600';
    }
  };

  const getGroundQualityTextColor = (category) => {
    switch(category) {
      case 'firm': return 'text-emerald-800';
      case 'soft': return 'text-amber-600';
      case 'wet': return 'text-amber-700';
      case 'waterlogged': return 'text-red-700';
      default: return 'text-amber-600';
    }
  };

  const getGroundQualityIcon = (category) => {
    switch(category) {
      case 'firm': return FiCheckCircle;
      case 'soft': return FiDroplet;
      case 'wet': return FiCloudRain;
      case 'waterlogged': return FiAlertCircle;
      default: return FiHelpCircle;
    }
  };

  const formatGroundQualityCategory = (category) => {
    if (!category) return 'Unknown';
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  // Get weather icon based on category
  const getWeatherIcon = (category) => {
    switch (category) {
      case 'excellent':
      case 'ideal':
      case 'very-good':
        return FiSun;
      case 'good':
      case 'okay':
      case 'fair':
        return FiCloud;
      case 'poor':
      case 'severe':
        return FiCloudRain;
      default:
        return FiCloud;
    }
  };

  // Main golf score badge colour
  // Design System: Uses amber-* for all warning states (never orange/yellow)
  const getScoreBadgeColor = (category) => {
    switch (category) {
      case 'excellent': return 'border-emerald-700 bg-emerald-50 text-emerald-800';
      case 'very-good': return 'border-emerald-600 bg-emerald-50 text-emerald-700';
      case 'ideal': return 'border-emerald-700 bg-emerald-50 text-emerald-800';
      case 'good': return 'border-amber-500 bg-amber-50 text-amber-700';
      case 'okay':
      case 'fair': return 'border-amber-600 bg-amber-50 text-amber-600';
      case 'poor': return 'border-amber-700 bg-amber-50 text-amber-700';
      case 'severe': return 'border-red-700 bg-red-50 text-red-700';
      default: return 'border-amber-600 bg-amber-50 text-amber-600';
    }
  };

  // Format category for display
  const formatCategory = (category) => {
    if (!category) return 'N/A';
    const formatted = category.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
    return formatted;
  };

  // Format time from ISO timestamp or HH:MM string
  const formatTime = (timeValue) => {
    if (!timeValue) return null;

    // If it's already HH:MM format, return as-is
    if (typeof timeValue === 'string' && /^\d{2}:\d{2}$/.test(timeValue)) {
      return timeValue;
    }

    // Try to parse as ISO timestamp
    try {
      const date = new Date(timeValue);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
      }
    } catch {
      // Fall through to return null
    }

    return null;
  };

  // Show unplayable warning if conditions are severe
  if (isUnplayable) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-200 bg-red-50"
      >
        <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-status-error" />
        <span className="text-sm font-medium text-status-error">Likely Unplayable</span>
      </motion.div>
    );
  }

  // No weather data available
  if (!golfScore && !golfCategory && temperature === null && temperature === undefined) {
    return null;
  }

  const WeatherIcon = getWeatherIcon(golfCategory);
  const tempCategory = getWeatherMetricCategory(temperature, 'temperature');
  const windCategory = getWeatherMetricCategory(windSpeed, 'windSpeed');
  const precipCategory = getWeatherMetricCategory(precipitation, 'precipitation');
  const humidityCategory = getWeatherMetricCategory(humidity, 'humidity');
  const uvCategory = getWeatherMetricCategory(uvIndex, 'uvIndex');

  const getUVLabel = (uv) => {
    if (uv == null) return '';
    if (uv <= 2) return 'Low';
    if (uv <= 5) return 'Moderate';
    if (uv <= 7) return 'High';
    if (uv <= 10) return 'Very High';
    return 'Extreme';
  };

  // Alert severity styling
  const getAlertSeverityConfig = (severity) => {
    switch (severity) {
      case 'extreme':
        return { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-800', icon: FiAlertCircle };
      case 'severe':
        return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: FiAlertCircle };
      case 'moderate':
        return { bg: 'bg-amber-100', border: 'border-amber-300', text: 'text-amber-800', icon: FiAlertTriangle };
      case 'minor':
      default:
        return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: FiAlertTriangle };
    }
  };

  // Get highest severity from alerts array
  const getHighestSeverity = (alertsList) => {
    const order = ['minor', 'moderate', 'severe', 'extreme'];
    return alertsList.reduce((max, alert) => {
      const idx = order.indexOf(alert.severity);
      return idx > order.indexOf(max) ? alert.severity : max;
    }, 'minor');
  };

  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const activeAlerts = (alerts || []).filter(a => a.expireTime > Date.now() / 1000);

  // Compact mode - just show the score badge (for inline use)
  if (compact) {
    const getCompactColor = (category) => {
      switch (category) {
        case 'excellent':
        case 'very-good':
        case 'good':
        case 'ideal': return 'text-emerald-700';
        case 'okay':
        case 'fair': return 'text-amber-600';
        case 'poor': return 'text-amber-700';
        case 'severe': return 'text-red-700';
        default: return 'text-text-secondary';
      }
    };

    const textClass = compactSize === 'md' ? 'text-base font-semibold' : 'text-xs font-medium';
    const iconClass = compactSize === 'md' ? 'w-4 h-4 mr-1.5' : 'w-3.5 h-3.5 mr-1';

    return (
      <span className={`inline-flex items-center ${textClass} ${getCompactColor(golfCategory)}`}>
        <SafeIcon icon={WeatherIcon} className={iconClass} />
        {golfScore?.toFixed(1) || '--'} {golfCategory ? golfCategory.replace('-', ' ').replace(/^\w/, c => c.toUpperCase()) : ''}
      </span>
    );
  }

  // Format sunrise/sunset times for display
  const sunriseTime = formatTime(sunrise);
  const sunsetTime = formatTime(sunset);
  const hasSunTimes = sunriseTime && sunsetTime;

  // Full grid display mode - EXACTLY matches SmartResultCard pattern
  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3"
    >
      {/* Weather Alerts Banner */}
      {activeAlerts.length > 0 && (() => {
        const severity = getHighestSeverity(activeAlerts);
        const config = getAlertSeverityConfig(severity);
        const AlertIcon = config.icon;

        return (
          <div className={`${config.bg} ${config.border} border rounded-xl overflow-hidden`}>
            <button
              onClick={() => setAlertsExpanded(!alertsExpanded)}
              className="w-full flex items-center justify-between px-4 py-2.5"
            >
              <div className="flex items-center gap-2">
                <SafeIcon icon={AlertIcon} className={`w-4 h-4 ${config.text}`} />
                <span className={`text-sm font-medium ${config.text}`}>
                  {activeAlerts.length} Weather Alert{activeAlerts.length > 1 ? 's' : ''}
                </span>
              </div>
              <SafeIcon
                icon={alertsExpanded ? FiChevronUp : FiChevronDown}
                className={`w-4 h-4 ${config.text}`}
              />
            </button>
            <AnimatePresence>
              {alertsExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-3 space-y-2">
                    {activeAlerts.map((alert) => {
                      const alertConfig = getAlertSeverityConfig(alert.severity);
                      return (
                        <div key={alert.id} className={`${alertConfig.bg} rounded-lg p-3 border ${alertConfig.border}`}>
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm font-medium ${alertConfig.text}`}>{alert.name}</p>
                            {alert.detailsUrl && (
                              <a
                                href={alert.detailsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`${alertConfig.text} opacity-60 hover:opacity-100 shrink-0`}
                              >
                                <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>
                          {alert.summary && (
                            <p className={`text-xs ${alertConfig.text} opacity-75 mt-1 line-clamp-3`}>{alert.summary}</p>
                          )}
                          {alert.source && (
                            <p className={`text-xs ${alertConfig.text} opacity-50 mt-1`}>Source: {alert.source}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })()}

      {/* Minute-by-Minute Precipitation Timeline (imminent rounds only) */}
      {minuteForecast?.length > 0 && (() => {
        const hasRain = minuteForecast.some(m => m.precipitationChance > 0.3);
        const maxIntensity = Math.max(...minuteForecast.map(m => m.precipitationIntensity));

        return (
          <div className={`rounded-xl border px-4 py-3 ${hasRain ? 'border-blue-200 bg-blue-50' : 'border-emerald-200 bg-emerald-50'}`}>
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiCloudRain} className={`w-4 h-4 ${hasRain ? 'text-blue-600' : 'text-emerald-600'}`} />
              <span className={`text-xs font-medium ${hasRain ? 'text-blue-700' : 'text-emerald-700'}`}>
                {hasRain ? 'Rain expected in the next hour' : 'No rain in the next hour'}
              </span>
            </div>
            <div className="flex items-end gap-px h-6">
              {minuteForecast.map((m, i) => {
                const intensity = maxIntensity > 0
                  ? Math.max(0.1, m.precipitationIntensity / maxIntensity)
                  : 0;
                const hasMinuteRain = m.precipitationChance > 0.3;
                return (
                  <div
                    key={i}
                    className={`flex-1 rounded-t-sm ${hasMinuteRain ? 'bg-blue-400' : 'bg-subtle'}`}
                    style={{ height: hasMinuteRain ? `${intensity * 100}%` : '15%' }}
                  />
                );
              })}
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-text-muted">Now</span>
              <span className="text-[10px] text-text-muted">+60 min</span>
            </div>
          </div>
        );
      })()}

      {/* Weather Metrics Grid - 2-col mobile, 3-col desktop by default; 6-col when `wide` (e.g. round detail full-width). */}
      <div className={`grid grid-cols-2 ${wide ? 'lg:grid-cols-6' : 'lg:grid-cols-3'} gap-3`}>
        {/* Temperature Card */}
        {temperature !== null && temperature !== undefined && (() => {
          const colorClass = getWeatherMetricColor(tempCategory);
          const iconColor = getCategoryIconColor(tempCategory);
          const textColor = getCategoryTextColor(tempCategory);
          const labelColor = getCategoryIconColor(tempCategory);

          return (
            <div className={`${colorClass} border px-4 py-3 rounded-xl text-center hover:shadow-xs transition-all duration-200`}>
              <SafeIcon icon={FiThermometer} className={`w-5 h-5 ${iconColor} mx-auto mb-2`} />
              <p className={`text-sm font-semibold ${textColor} whitespace-nowrap`}>
                {Math.round(temperature)}°C
              </p>
              <p className={`text-xs ${labelColor} mt-1 whitespace-nowrap`}>Temperature</p>
            </div>
          );
        })()}

        {/* Wind Speed Card */}
        {windSpeed !== null && windSpeed !== undefined && (() => {
          const colorClass = getWeatherMetricColor(windCategory);
          const iconColor = getCategoryIconColor(windCategory);
          const textColor = getCategoryTextColor(windCategory);
          const labelColor = getCategoryIconColor(windCategory);

          return (
            <div className={`${colorClass} border px-4 py-3 rounded-xl text-center hover:shadow-xs transition-all duration-200`}>
              <SafeIcon icon={FiWind} className={`w-5 h-5 ${iconColor} mx-auto mb-2`} />
              <p className={`text-sm font-semibold ${textColor} whitespace-nowrap`}>
                {Math.round(windSpeed)} mph
              </p>
              <p className={`text-xs ${labelColor} mt-1 whitespace-nowrap`}>Wind Speed</p>
            </div>
          );
        })()}

        {/* Precipitation Card */}
        {precipitation !== null && precipitation !== undefined && (() => {
          const colorClass = getWeatherMetricColor(precipCategory);
          const iconColor = getCategoryIconColor(precipCategory);
          const textColor = getCategoryTextColor(precipCategory);
          const labelColor = getCategoryIconColor(precipCategory);
          // Use full label from prop, or default to 'Rain'
          const label = precipitationLabel || 'Rain';

          return (
            <div className={`${colorClass} border px-4 py-3 rounded-xl text-center hover:shadow-xs transition-all duration-200`}>
              <SafeIcon icon={FiCloudRain} className={`w-5 h-5 ${iconColor} mx-auto mb-2`} />
              <p className={`text-sm font-semibold ${textColor} whitespace-nowrap`}>
                {Math.round(precipitation)} mm
              </p>
              <p className={`text-xs ${labelColor} mt-1 whitespace-nowrap`}>{label}</p>
            </div>
          );
        })()}

        {/* Humidity Card */}
        {humidity != null && (() => {
          const colorClass = getWeatherMetricColor(humidityCategory);
          const iconColor = getCategoryIconColor(humidityCategory);
          const textColor = getCategoryTextColor(humidityCategory);
          const labelColor = getCategoryIconColor(humidityCategory);

          return (
            <div className={`${colorClass} border px-4 py-3 rounded-xl text-center hover:shadow-xs transition-all duration-200`}>
              <SafeIcon icon={FiDroplet} className={`w-5 h-5 ${iconColor} mx-auto mb-2`} />
              <p className={`text-sm font-semibold ${textColor} whitespace-nowrap`}>
                {Math.round(humidity)}%
              </p>
              <p className={`text-xs ${labelColor} mt-1 whitespace-nowrap`}>Humidity</p>
            </div>
          );
        })()}

        {/* UV Index Card (WeatherKit only) */}
        {uvIndex != null && (() => {
          const colorClass = getWeatherMetricColor(uvCategory);
          const iconColor = getCategoryIconColor(uvCategory);
          const textColor = getCategoryTextColor(uvCategory);
          const labelColor = getCategoryIconColor(uvCategory);

          return (
            <div className={`${colorClass} border px-4 py-3 rounded-xl text-center hover:shadow-xs transition-all duration-200`}>
              <SafeIcon icon={FiSun} className={`w-5 h-5 ${iconColor} mx-auto mb-2`} />
              <p className={`text-sm font-semibold ${textColor} whitespace-nowrap`}>
                {Math.round(uvIndex)}
              </p>
              <p className={`text-xs ${labelColor} mt-1 whitespace-nowrap`}>{getUVLabel(uvIndex)} UV</p>
            </div>
          );
        })()}

        {/* Ground Quality Card (if available) - EXACT match to SmartResultCard */}
        {groundCategory && (() => {
          const colorClass = getGroundQualityColor(groundCategory);
          const iconColor = getGroundQualityIconColor(groundCategory);
          const textColor = getGroundQualityTextColor(groundCategory);
          const labelColor = getGroundQualityIconColor(groundCategory);
          const GroundIcon = getGroundQualityIcon(groundCategory);

          return (
            <div className={`${colorClass} border px-4 py-3 rounded-xl text-center hover:shadow-xs transition-all duration-200`}>
              <SafeIcon icon={GroundIcon} className={`w-5 h-5 ${iconColor} mx-auto mb-2`} />
              <p className={`text-sm font-semibold ${textColor} whitespace-nowrap`}>
                {formatGroundQualityCategory(groundCategory)}
              </p>
              <p className={`text-xs ${labelColor} mt-1 whitespace-nowrap`}>Ground</p>
            </div>
          );
        })()}

        {/* Daylight Card (Sunrise / Sunset combined) - EXACT match to SmartResultCard */}
        {(hasSunTimes || daylightCategory) && (() => {
          // Use daylightCategory for colouring (maps to 6-tier system)
          const colorClass = getWeatherMetricColor(daylightCategory || 'okay');
          const iconColor = getCategoryIconColor(daylightCategory || 'okay');
          const textColor = getCategoryTextColor(daylightCategory || 'okay');
          const labelColor = getCategoryIconColor(daylightCategory || 'okay');

          return (
            <div className={`${colorClass} border px-4 py-3 rounded-xl text-center hover:shadow-xs transition-all duration-200`}>
              <div className="flex items-center justify-center gap-1 mb-2">
                <SafeIcon icon={FiSunrise} className={`w-4 h-4 ${iconColor}`} />
                <span className="text-text-muted">/</span>
                <SafeIcon icon={FiSunset} className={`w-4 h-4 ${iconColor}`} />
              </div>
              <p className={`text-sm font-semibold ${textColor} whitespace-nowrap`}>
                {hasSunTimes ? `${sunriseTime} – ${sunsetTime}` : formatCategory(daylightCategory)}
              </p>
              <p className={`text-xs ${labelColor} mt-1 whitespace-nowrap`}>Daylight</p>
            </div>
          );
        })()}
      </div>

      {/* Apple Weather Attribution (required by WeatherKit terms) */}
      <p className="text-[10px] text-text-muted text-right mt-1">
        Weather data by{' '}
        <a
          href="https://weatherkit.apple.com/legal-attribution.html"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-text-secondary"
        >
          Apple Weather
        </a>
      </p>
    </motion.div>
  );
};

export default WeatherBadge;
